import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  HttpUtilsService,
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
  QueryResultsModel,
} from '../../_base/crud';
import { Template } from '../_models/template.model';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth';
import { environment } from 'src/environments/environment';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  TaskTemplate,
  TaskTemplateActionMapperKey,
  TaskTemplateStep,
} from '../../../views/pages/calendar/models';

@Injectable()
export class TemplateService {
  private authToken;
  private company_id;
  private openDialogs: MatDialogRef<any>[] = [];

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private layoutUtils: LayoutUtilsService,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.company_id = res.companyID));
  }

  private static getTemplatesPageQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}`;
    } else {
      if (filter.id) fromString += `&id_has=${filter.id}`;
      if (filter.name) fromString += `&name_has=${filter.name}`;
      if (filter.categoryId) fromString += `&category_id=${filter.categoryId}`;
      if (filter.isBackstage) fromString += `&is_backstage=${filter.isBackstage}`;
      if (filter.isIntervention) fromString += `&is_intervention=${filter.isIntervention}`;
      if (filter.additionalStepOnly) fromString += `&additional_only=${filter.additionalStepOnly}`;
      if (filter.allRoles) fromString += `&all_roles=${filter.allRoles}`;
      if (filter.hasResources) fromString += `&has_resources=${filter.hasResources}`;
    }

    return fromString;
  }

  getTemplatesPage(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({
      fromString: TemplateService.getTemplatesPageQueryString(queryParams),
    });

    return this.http.get<QueryResultsModel>(
      `${environment.TIMELINE_BASE}/companies/${this.company_id}/task_templates`,
      { params },
    );
  }

  createTemplate(_template: Template) {
    const url = `${environment.TIMELINE_BASE}/companies/${this.company_id}/task_templates`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<any>(url, _template, { headers });
  }

  getTemplatesByIds(templateIds: number[]): Observable<any> {
    let fromString = `all_categories=true&page=0&size=${templateIds.length}`;

    templateIds.forEach((id) => (fromString += `&id=${id}`));

    const params = new HttpParams({ fromString });

    return this.http.get<any>(
      `${environment.TIMELINE_BASE}/companies/${this.company_id}/task_templates`,
      { params },
    );
  }

  updateTemplate(_template: Template): Observable<any> {
    const url = `${environment.TIMELINE_BASE}/companies/${this.company_id}/task_templates/${_template.id}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.put<Template>(url, _template, { headers });
  }

  duplicateTemplate(templateId: number, name: string): Observable<{ data: TaskTemplate }> {
    return this.http.post<{ data: TaskTemplate }>(
      `${environment.TIMELINE_BASE}/companies/${this.company_id}/task_templates/${templateId}/copy`,
      {
        name,
      },
    );
  }

  deleteTemplate(templateId: number): Observable<any> {
    const url = `${environment.TIMELINE_BASE}/companies/${this.company_id}/task_templates/${templateId}`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.delete<any>(url, { headers });
  }

  searchTemplates(category_id, search_term) {
    const url = `${environment.TIMELINE_BASE}/companies/${this.company_id}/task_templates`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const params = new HttpParams()
      .append('sort', 'name,asc')
      .append('category_id', category_id)
      .append('search_term', search_term)
      .append('size', '100');
    return this.http.get<any>(url, { headers, params });
  }
  searchAllTemplates(search_term) {
    const url = `${environment.TIMELINE_BASE}/companies/${this.company_id}/task_templates`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const params = new HttpParams()
      .append('sort', 'name,asc')
      .append('search_term', search_term)
      .append('size', '100');
    return this.http.get<any>(url, { headers, params });
  }

  searchRelatedActivityTemplates(task_template_id, search_term) {
    const url = `${environment.ACTIVITY_TRACKING_BASE}/companies/${this.company_id}/related-activity-template/autocomplete/${task_template_id}?search_term=${search_term}`;
    return this.http.get<any>(url);
  }

  templatesAutocomplete(search_term) {
    const url = `${environment.TIMELINE_BASE}/companies/${this.company_id}/task_templates/autocomplete`;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const params = new HttpParams().append('search_term', search_term);
    return this.http.get<any>(url, { headers, params });
  }
  openDialog(dialog, config): MatDialogRef<any> {
    const dialogRef = this.dialog.open(dialog, config);
    this.openDialogs.push(dialogRef);
    return dialogRef;
  }

  closeLatestDialog(result): void {
    const dialogRef = this.openDialogs.pop();
    if (dialogRef) {
      dialogRef.close(result);
      if (result.view) return;
      if (result.updated) {
        this.layoutUtils.showActionNotification(
          'Template updated successfully',
          MessageType.Update,
          3000,
          false,
          false,
        );
        return;
      }
      this.layoutUtils.showActionNotification(
        'Template added successfully',
        MessageType.Create,
        3000,
        false,
        false,
      );
    }
  }
  getTemplateById(id: number): Observable<any> {
    const fromString = `id=${id}`;
    const params = new HttpParams({ fromString });
    return this.http.get<any>(
      `${environment.TIMELINE_BASE}/companies/${this.company_id}/task_templates`,
      { params },
    );
  }

  // Task V3 Template APIs

  findTaskTemplates(filter, searchTerm: string): Observable<{ data: TaskTemplate[] }> {
    let fromString = `search_term=${searchTerm || ''}`;

    if (filter.categoryId) fromString += `&category_id=${filter.categoryId}`;
    if (filter.allCategories?.toString()) fromString += `&all_categories=${filter.allCategories}`;
    if (filter.isIntervention?.toString())
      fromString += `&is_intervention=${filter.isIntervention}`;
    if (filter.isService?.toString()) fromString += `&is_service=${filter.isService}`;

    const params = new HttpParams({ fromString });

    return this.http.get<{ data: TaskTemplate[] }>(
      `${environment.TIMELINE_BASE}/companies/${this.company_id}/task_templates${
        filter.notAdditionalStep ? '/not-step' : ''
      }`,
      { params },
    );
  }

  getTaskTemplate(templateId: number): Observable<{ data: TaskTemplate }> {
    return this.http.get<{ data: TaskTemplate }>(
      `${environment.TIMELINE_BASE}/companies/${this.company_id}/task_templates/${templateId}`,
    );
  }

  getRelatedTaskTemplates(
    templateId: number,
  ): Observable<{ data: { task_templates: TaskTemplate[] } }> {
    return this.http.get<{ data: { task_templates: TaskTemplate[] } }>(
      `${environment.TIMELINE_BASE}/companies/${this.company_id}/related-tasks-template/${templateId}`,
    );
  }
  getRelationTypes(entityTypes: string[]): Observable<any> {
    let params = new HttpParams();
    entityTypes.forEach((entityType) => {
      params = params.append('entity_type', entityType);
    });
    return this.http.get(`${environment.CORE_BASE}/companies/${this.company_id}/relation_types`, {
      params,
    });
  }

  getTaskTemplateSteps(
    templateId: number,
  ): Observable<{ data: { additional_step_template_dtos: TaskTemplateStep[] } }> {
    return this.http.get<{ data: { additional_step_template_dtos: TaskTemplateStep[] } }>(
      `${environment.TIMELINE_BASE}/companies/${this.company_id}/additional_steps_template/${templateId}`,
    );
  }

  getTaskTemplateActionMapperKeys(
    taskId: number,
  ): Observable<{ data: TaskTemplateActionMapperKey[] }> {
    return this.http.get<{ data: TaskTemplateActionMapperKey[] }>(
      `${environment.TIMELINE_BASE}/v3/companies/${this.company_id}/tasks/keys-used/${taskId}`,
    );
  }
}
